import { useState } from 'react';
import { Download, Paperclip, Plus, Receipt } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api, withAuthToken } from '@/lib/api';
import { useExpenseCategories, useExpenses } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fmtMoney, todayISO } from '@/lib/format';
import { PhotoUploader } from '@/components/PhotoUploader';
import { buildCsv, downloadCsv } from '@/lib/csv';
import { PageHeader } from '@/components/PageHeader';

export const ExpensesPage = () => {
  const { data: items = [] } = useExpenses();
  const total = items.reduce((s: number, e: any) => s + Number(e.amount), 0);

  return (
    <div className="animate-in-up space-y-4">
      <PageHeader
        title="Расходы"
        subtitle={items.length ? `${items.length} операций` : undefined}
        right={
          <div className="flex items-center gap-2">
            {items.length ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadCsv(
                    `chernova-expenses_${todayISO()}`,
                    buildCsv(items, [
                      {
                        header: 'Дата',
                        value: (e: any) => new Date(e.date).toLocaleDateString('ru-RU'),
                      },
                      { header: 'Категория', value: (e: any) => e.category.name },
                      { header: 'Сумма', value: (e: any) => Number(e.amount) },
                      { header: 'Описание', value: (e: any) => e.description ?? '' },
                    ]),
                  )
                }
              >
                <Download className="h-3.5 w-3.5" /> CSV
              </Button>
            ) : null}
            <NewExpenseDialog />
          </div>
        }
      />

      <div className="relative overflow-hidden rounded-3xl bg-brand p-5 text-white shadow-soft-lg">
        <div
          aria-hidden
          className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl"
        />
        <div className="relative">
          <div className="text-xs font-bold uppercase tracking-widest opacity-90">
            Всего расходов
          </div>
          <div className="mt-1 text-4xl font-bold tabular leading-tight">
            {fmtMoney(total)}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((e: any) => (
          <ExpenseRow key={e.id} expense={e} />
        ))}
        {!items.length && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-soft text-accent">
                <Receipt className="h-6 w-6" />
              </div>
              <div>
                <div className="font-semibold">Расходы не добавлены</div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Добавляйте траты — увидите чистую прибыль в статистике
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

    </div>
  );
};

const ExpenseRow = ({ expense: e }: { expense: any }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Card
        className="cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-soft-md active:scale-[0.99]"
        onClick={() => setOpen(true)}
      >
        <CardContent className="flex items-center gap-3 p-3">
          {e.receipt_url ? (
            <img
              src={withAuthToken(e.receipt_url)}
              className="h-12 w-12 shrink-0 rounded-xl object-cover"
            />
          ) : (
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: `${e.category.color ?? '#a855f7'}1f`,
                color: e.category.color ?? '#a855f7',
              }}
            >
              <Paperclip className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{e.description ?? e.category.name}</div>
            <div className="text-xs text-muted-foreground">
              {new Date(e.date).toLocaleDateString('ru-RU')} · {e.category.name}
            </div>
          </div>
          <div className="text-base font-bold tabular">{fmtMoney(Number(e.amount))}</div>
        </CardContent>
      </Card>
      <ExpenseDetailsDialog expense={e} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const ExpenseDetailsDialog = ({
  expense,
  open,
  onClose,
}: {
  expense: any;
  open: boolean;
  onClose: () => void;
}) => {
  const qc = useQueryClient();
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {fmtMoney(Number(expense.amount))} · {expense.category.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {new Date(expense.date).toLocaleDateString('ru-RU')}
          </div>
          {expense.description && <div className="text-sm">{expense.description}</div>}
          <PhotoUploader
            label="Чек"
            uploadUrl={`/expenses/${expense.id}/receipt`}
            currentUrl={expense.receipt_url}
            onUploaded={() => qc.invalidateQueries({ queryKey: ['expenses'] })}
          />
          <Button
            variant="destructive"
            className="w-full"
            onClick={async () => {
              try {
                await api.delete(`/expenses/${expense.id}`);
                qc.invalidateQueries({ queryKey: ['expenses'] });
                onClose();
              } catch (e: any) {
                alert(e?.response?.data?.error ?? 'Не удалось удалить расход');
              }
            }}
          >
            Удалить
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NewExpenseDialog = () => {
  const [open, setOpen] = useState(false);
  const { data: cats = [] } = useExpenseCategories();
  const [date, setDate] = useState(todayISO());
  const [categoryId, setCategoryId] = useState<string>('');
  const [amount, setAmount] = useState<number>(0);
  const [desc, setDesc] = useState('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const submit = async () => {
    if (submitting) return;
    setError(null);
    if (!categoryId) {
      setError('Выберите категорию');
      return;
    }
    if (!amount || amount <= 0) {
      setError('Укажите сумму');
      return;
    }
    setSubmitting(true);
    try {
      const { data: created } = await api.post('/expenses', {
        date,
        category_id: categoryId,
        amount,
        description: desc || undefined,
      });
      if (receiptFile && created?.id) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        await api.post(`/expenses/${created.id}/receipt`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      qc.invalidateQueries({ queryKey: ['expenses'] });
      setOpen(false);
      setAmount(0);
      setDesc('');
      setReceiptFile(null);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Ошибка сохранения расхода');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon-sm" aria-label="Новый расход">
          <Plus className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Новый расход</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Дата</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Категория</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {cats.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Сумма (₽)</Label><Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} /></div>
          <div><Label>Описание</Label><Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          <div>
            <Label>Чек (фото)</Label>
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/50 p-3 transition-colors hover:border-primary hover:bg-primary-soft/40">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-card text-primary">
                <Paperclip className="h-4 w-4" />
              </span>
              <span className="flex-1 text-sm">
                {receiptFile ? receiptFile.name : 'Прикрепить фото чека'}
              </span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
                className="hidden"
              />
            </label>
          </div>
          {error && <div className="text-xs text-destructive">{error}</div>}
          <Button
            onClick={submit}
            disabled={submitting || !categoryId || !amount}
            size="lg"
            className="w-full"
          >
            <Plus className="h-4 w-4" /> Сохранить расход
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
